/**
 * Breakpoint System
 *
 * Desktop-first approach with responsive support.
 * Following industry-standard naming conventions aligned with Tailwind CSS.
 *
 * Design Philosophy:
 * - Desktop-first: Default styles are for desktop, use max-width for smaller screens
 * - Content-based: Breakpoints chosen to support common device widths
 * - Semantic naming: xs, sm, md, lg, xl, 2xl for consistency
 *
 * Common Device Widths (reference):
 * - Mobile S: 320px
 * - Mobile M: 375px
 * - Mobile L: 425px
 * - Tablet: 768px
 * - Laptop: 1024px
 * - Laptop L: 1440px
 * - 4K: 2560px
 */

// =============================================================================
// BREAKPOINT VALUES (in pixels)
// =============================================================================

/**
 * Breakpoint values following Tailwind CSS conventions.
 * These represent the MINIMUM width for each breakpoint.
 *
 * Usage:
 * - xs: Extra small devices (phones in portrait)
 * - sm: Small devices (phones in landscape, small tablets)
 * - md: Medium devices (tablets)
 * - lg: Large devices (laptops, desktops)
 * - xl: Extra large devices (large desktops)
 * - 2xl: Ultra wide screens
 */
export const BREAKPOINTS = {
  xs: 0, // 0px - 479px (extra small, mobile portrait)
  sm: 480, // 480px - 639px (small mobile, landscape)
  md: 640, // 640px - 767px (large mobile, small tablet)
  lg: 768, // 768px - 1023px (tablet)
  xl: 1024, // 1024px - 1279px (laptop, small desktop)
  '2xl': 1280, // 1280px - 1535px (desktop)
  '3xl': 1536, // 1536px+ (large desktop, ultra wide)
} as const;

export type BreakpointKey = keyof typeof BREAKPOINTS;

// =============================================================================
// MEDIA QUERY HELPERS
// =============================================================================

/**
 * Generate min-width media query (mobile-first)
 * Use when you want styles to apply AT this breakpoint and ABOVE
 *
 * @example
 * // CSS: Applies at 768px and wider
 * @media ${mediaUp('lg')} { ... }
 */
export function mediaUp(breakpoint: BreakpointKey): string {
  const value = BREAKPOINTS[breakpoint];
  if (value === 0) return 'all'; // xs applies to all
  return `(min-width: ${value}px)`;
}

/**
 * Generate max-width media query (desktop-first)
 * Use when you want styles to apply BELOW this breakpoint
 *
 * @example
 * // CSS: Applies below 768px (mobile and small tablets)
 * @media ${mediaDown('lg')} { ... }
 */
export function mediaDown(breakpoint: BreakpointKey): string {
  const value = BREAKPOINTS[breakpoint];
  if (value === 0) return 'none'; // Can't go below 0
  return `(max-width: ${value - 0.02}px)`;
}

/**
 * Generate media query for a specific range
 * Use when you want styles to apply only BETWEEN two breakpoints
 *
 * @example
 * // CSS: Applies between 768px and 1023px (tablet only)
 * @media ${mediaBetween('lg', 'xl')} { ... }
 */
export function mediaBetween(lower: BreakpointKey, upper: BreakpointKey): string {
  const lowerValue = BREAKPOINTS[lower];
  const upperValue = BREAKPOINTS[upper];
  return `(min-width: ${lowerValue}px) and (max-width: ${upperValue - 0.02}px)`;
}

/**
 * Generate media query for a specific breakpoint only
 * Use when you want styles to apply ONLY at this breakpoint range
 *
 * @example
 * // CSS: Applies only at tablet sizes (768px - 1023px)
 * @media ${mediaOnly('lg')} { ... }
 */
export function mediaOnly(breakpoint: BreakpointKey): string {
  const keys = Object.keys(BREAKPOINTS) as BreakpointKey[];
  const currentIndex = keys.indexOf(breakpoint);
  const nextBreakpoint = keys[currentIndex + 1];

  if (!nextBreakpoint) {
    return mediaUp(breakpoint);
  }

  return mediaBetween(breakpoint, nextBreakpoint);
}

// =============================================================================
// CSS MEDIA QUERY STRINGS (for use in SCSS/CSS)
// =============================================================================

/**
 * Pre-built media query strings for CSS/SCSS usage.
 * Import and use directly in styled-components, emotion, or SCSS mixins.
 */
export const mediaQueries = {
  // Mobile-first (min-width) - "and up"
  smUp: mediaUp('sm'),
  mdUp: mediaUp('md'),
  lgUp: mediaUp('lg'),
  xlUp: mediaUp('xl'),
  '2xlUp': mediaUp('2xl'),
  '3xlUp': mediaUp('3xl'),

  // Desktop-first (max-width) - "and down"
  xsDown: mediaDown('sm'),
  smDown: mediaDown('md'),
  mdDown: mediaDown('lg'),
  lgDown: mediaDown('xl'),
  xlDown: mediaDown('2xl'),
  '2xlDown': mediaDown('3xl'),

  // Specific ranges only
  xsOnly: mediaOnly('xs'),
  smOnly: mediaOnly('sm'),
  mdOnly: mediaOnly('md'),
  lgOnly: mediaOnly('lg'),
  xlOnly: mediaOnly('xl'),
  '2xlOnly': mediaOnly('2xl'),

  // Common semantic aliases
  mobile: mediaDown('lg'), // Below tablet (< 768px)
  tablet: mediaBetween('lg', 'xl'), // Tablet only (768px - 1023px)
  tabletUp: mediaUp('lg'), // Tablet and above (>= 768px)
  desktop: mediaUp('xl'), // Desktop and above (>= 1024px)
  largeDesktop: mediaUp('2xl'), // Large desktop (>= 1280px)
  ultraWide: mediaUp('3xl'), // Ultra wide (>= 1536px)
} as const;

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Check if a window width matches a breakpoint (for JS logic)
 */
export function isBreakpoint(width: number, breakpoint: BreakpointKey): boolean {
  const keys = Object.keys(BREAKPOINTS) as BreakpointKey[];
  const currentIndex = keys.indexOf(breakpoint);
  const nextBreakpoint = keys[currentIndex + 1];
  const minWidth = BREAKPOINTS[breakpoint];
  const maxWidth = nextBreakpoint ? BREAKPOINTS[nextBreakpoint] - 1 : Infinity;

  return width >= minWidth && width <= maxWidth;
}

/**
 * Get the current breakpoint name based on window width
 */
export function getCurrentBreakpoint(width: number): BreakpointKey {
  const entries = Object.entries(BREAKPOINTS) as [BreakpointKey, number][];
  const sorted = entries.sort((a, b) => b[1] - a[1]); // Sort descending

  for (const [key, value] of sorted) {
    if (width >= value) {
      return key;
    }
  }

  return 'xs';
}

/**
 * Check if current width is at or above a breakpoint
 */
export function isAbove(width: number, breakpoint: BreakpointKey): boolean {
  return width >= BREAKPOINTS[breakpoint];
}

/**
 * Check if current width is below a breakpoint
 */
export function isBelow(width: number, breakpoint: BreakpointKey): boolean {
  return width < BREAKPOINTS[breakpoint];
}

// =============================================================================
// SCSS VARIABLES EXPORT (for use in vite plugin)
// =============================================================================

/**
 * Generate SCSS variables for breakpoints
 */
export function generateBreakpointSCSS(): string {
  const lines: string[] = [];

  // Breakpoint values
  Object.entries(BREAKPOINTS).forEach(([key, value]) => {
    lines.push(`$breakpoint-${key}: ${value}px;`);
  });

  lines.push('');

  // Breakpoint map
  lines.push('$breakpoints: (');
  Object.entries(BREAKPOINTS).forEach(([key, value], index, arr) => {
    const comma = index < arr.length - 1 ? ',' : '';
    lines.push(`  ${key}: ${value}px${comma}`);
  });
  lines.push(');');

  return lines.join('\n');
}

/**
 * Generate CSS custom properties for breakpoints (for runtime access)
 */
export function generateBreakpointCSSVars(): Record<string, string> {
  const vars: Record<string, string> = {};
  Object.entries(BREAKPOINTS).forEach(([key, value]) => {
    vars[`breakpoint-${key}`] = `${value}px`;
  });
  return vars;
}
